<script setup lang="ts">
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import { GripVertical, Pencil } from 'lucide-vue-next'

import MissionEditorDialog from './MissionEditorDialog.vue'
import PreviewZone from './PreviewZone.vue'
import { MISSION_HEROICONS } from './missionIcons'
import { renderMissionText } from './missionText'
import useMissionInstances from '../composables/missionInstances'

import type { WebappMission } from '@/types/types'

const { t, locale } = useI18n()
const { fetchInstances, instancesFor, errorFor, needsFetch, isLoading: isLoadingInstances } = useMissionInstances()
const { user } = useLocalStorage()

const props = withDefaults(defineProps<{ hasError?: boolean; color?: string; pointsLabel?: string }>(), {
  pointsLabel: 'pts'
})

const emit = defineEmits<{ (e: 'save'): void }>()

const missions = defineModel<WebappMission[]>({ required: true })

const editingIndex = ref<number | null>(null)
const isEditorOpen = ref(false)

const editingMission = computed({
  get: () => (editingIndex.value === null ? null : missions.value[editingIndex.value]),
  set: (value: WebappMission | null) => {
    if (editingIndex.value === null || value === null) return
    const next = [...missions.value]
    next[editingIndex.value] = value
    missions.value = next
  }
})

const openEditor = (index: number) => {
  editingIndex.value = index
  isEditorOpen.value = true
  fetchInstances(needsFetch(missions.value[index].type) ? missions.value[index].type : false)
}

const render = (mission: WebappMission, field: 'title' | 'subtitle') =>
  renderMissionText(mission[field], locale.value, {
    points: mission.points,
    label: props.pointsLabel,
    merchantName: user.value.merchant.name ?? ''
  })

const renderedMissions = computed(() =>
  missions.value.map((mission) => ({ title: render(mission, 'title'), subtitle: render(mission, 'subtitle') }))
)

onMounted(fetchInstances)
</script>

<template>
  <PreviewZone zone="loyalty" :padded="false">
    <div class="flex flex-col gap-1 p-5">
      <h3 class="font-sans text-base font-semibold">{{ t('pages.loyaltySupports.webapp.missions.listTitle') }}</h3>
      <p class="text-sm text-muted-foreground">{{ t('pages.loyaltySupports.webapp.missions.listDescription') }}</p>
    </div>

    <p v-if="hasError" class="text-destructive px-5 pb-4 text-sm">
      {{ t('pages.loyaltySupports.webapp.missions.loadError') }}
    </p>

    <draggable v-model="missions" handle=".mission-handle" class="flex flex-col">
      <div
        v-for="(mission, index) in missions"
        :key="mission.type ?? `custom-${mission.id ?? index}`"
        class="flex items-center gap-4 border-t border-border px-5 py-4"
        :class="!mission.isVisible && 'opacity-50'"
      >
        <GripVertical class="mission-handle size-4 shrink-0 cursor-grab text-muted-foreground/60" />

        <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted">
          <component
            :is="MISSION_HEROICONS[mission.iconValue] ?? MISSION_HEROICONS.SparklesIcon"
            v-if="mission.iconType === 'heroicon'"
            class="size-5 text-foreground-secondary"
            aria-hidden="true"
          />
          <span v-else class="text-lg leading-none" aria-hidden="true">{{ mission.iconValue }}</span>
        </div>

        <div class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-sm font-medium text-foreground">{{ renderedMissions[index].title }}</span>
          <span v-if="renderedMissions[index].subtitle" class="truncate text-xs text-muted-foreground">
            {{ renderedMissions[index].subtitle }}
          </span>
        </div>

        <Badge v-if="mission.points > 0" variant="secondary" class="bg-primary/5 text-primary shrink-0">
          {{ mission.points }} {{ t('pages.loyaltySupports.webapp.missions.pointsShort') }}
        </Badge>

        <BaseSwitch :id="`mission-visible-${index}`" v-model="mission.isVisible" />

        <Button
          variant="ghost"
          size="icon-sm"
          class="shrink-0"
          :aria-label="t('global.words.edit')"
          @click="openEditor(index)"
        >
          <Pencil />
        </Button>
      </div>
    </draggable>

    <MissionEditorDialog
      v-if="editingMission"
      v-model="editingMission"
      v-model:open="isEditorOpen"
      :instances="instancesFor(editingMission.type)"
      :is-loading-instances="isLoadingInstances"
      :instances-error="errorFor(editingMission.type)"
      :color="color"
      :points-label="pointsLabel"
      @saved="emit('save')"
      @refresh-instances="fetchInstances(editingMission?.type ?? true)"
    />
  </PreviewZone>
</template>
